/**
 * @see https://wiki.domrf.ru/pages/viewpage.action?pageId=205101808
 */

import dayjs from 'dayjs';
import { onlyUnique } from '../utils';
import axios from 'axios';

const isSameOrBefore = require('dayjs/plugin/isSameOrBefore');
const isBetween = require('dayjs/plugin/isBetween')
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween)


const EOfficeCode = {
    manager: '0000',
};

const EVENT_DAYS = [
    '30.12.2023',
    '31.12.2023',
    '01.01.2024',
    '02.01.2024',
    '03.01.2024',
    '04.01.2024',
    '05.01.2024',
    '06.01.2024',
    '07.01.2024',
    '08.01.2024'
];

// рабочие дни-исключения
const WORKED_DAYS = [
    '02.11.2024'
]

// TODO
// добавить вывод дат из админки, из инфоблока Регион
const BLOCKED_REGION_DAYS = [
    {
        region: 146395, // Татарстан
        days: ['06.11.2024']
    }
]

/**
 * Проверяет что код города доставки - Москва
 * @param {number} city
 */
const isMoscow = (city) => {
    return city === 146475;
};

/**
 * Проверяет код региона и дату для блокировки даты в указанном регионе
 * @param {number} currentRegion - выбранный пользователем регион
 * @param {Date} currentDate - дата в календаре
 */
const isBlockedRegionAndDays = (currentRegion, currentDay) => {
    let region = BLOCKED_REGION_DAYS.find(item => item.region === currentRegion);
    currentDay = dayjs(currentDay).format('DD.MM.YYYY');
    return region?.days.includes(currentDay)
}

/**
 * Проверяет код выездного менеджера
 * @param {string} office - office code
 */
const isManager = (office) => office === EOfficeCode.manager;

/**
 * **Заявка в будни** <br>
 * При подаче заявки до 16:00 текущего дня, у клиента есть возможность осуществить запись на следующий рабочий день. <br>
 * При создании заявки после 16:00 доступна запись только через день.
 * @param {Date} day
 * @param {string} office - office code
 * @returns {boolean} true если день недоступен
 */
const isDayAfterTomorrow = (day) => new Date().getHours() >= 16
    && (dayjs().add(1, 'day').format('YYYY-DD-MM') === dayjs(day).format('YYYY-DD-MM'));

/**
 * блокируем **СЛЕДУЮЩИЙ** понедельник, если клиент оформляет заявку в пятницу после 16:00 или если в субботу или воскресенье весь день
 * @param {Date} day
 * @returns {boolean} true если день недоступен
 */
const blockNextMonday = (day) => {
    const today = new Date();
    return day.getDay() === 1 && dayjs(day).diff(dayjs(), 'day') < 7 && (today.getDay() === 5 && today.getHours() >= 16 || [0, 6].includes(today.getDay()));
};

/**
 * Доставка доступна на ближайшие 14 суток от текущей даты
 * @param {Date} day
 * @returns {boolean} true если день недоступен
 */
const isAfter2Weeks = (day) => dayjs(day).isAfter(dayjs().add(14, 'day'), 'day');


/**
 * Недоступна доставка день-в-день
 * @param {Date} day
 * @returns {boolean} true если день недоступен
 */
const isTodayAndBefore = (day) => dayjs(day).isSameOrBefore(dayjs());


/**
 * Недоступны предыдущие дни
 * @param {Date} day
 * @returns {boolean} true если день недоступен
 */
const isBefore = (day) => dayjs(day).isBefore(dayjs().subtract(1, 'day'));


/**
 * Доставка в воскресенье заблокирована везде
 * @param {Date} day
 * @returns {boolean} true если день недоступен
 */
const isSunday = (day) => {
    return day.getDay() === 0;
};

/**
 * Доставка в субботу и воскресенье всегда закрыта
 * @param {Date} day
 * @returns {boolean} true если день недоступен
 */
const isHolidays = (day) => {
    return [0, 6].includes(day.getDay());
};

/**
 * **Заявка в выходные** <br>
 * Заказ доставки на субботу текущей недели возможно осуществить в любой день предшествующий дате доставке в субботу <br>
 * При этом в пятницу подать заявку на сайте для доставки на следующий день в субботу клиент имеет возможность до 16:00 <br>
 * После 16:00 МСК в пятницу, доставка возможна в субботу следующей недели
 * @param {Date} day
 * @returns {boolean} true если день недоступен
 */
const isHolidaysRules = (day) => {
    const today = new Date();

    return isHolidays(day)
        && (
            (isHolidays(today) && !dayjs(day).isAfter(dayjs(today).add(2, 'day'), 'day'))
            || (today.getDay() === 5 && today.getHours() >= 16 && dayjs(day).isBefore(dayjs().add(7, 'day'), 'day'))
        )
};

/**
 * **Заявка в праздники** <br>
 * Если заявка поступает до 16:00, то доставка доступна на следующий день после праздника <br>
 * Если заявка поступает после 16:00, то доставка доступна через 1 день после праздника
 * @param {Date} day
 * @param {string[]} eventDays - правздники, список дат в формате `['DD.MM.YYYY', ...]`
 * @returns {boolean} true если день недоступен
 */
const isEventDays = (day, eventDays) => {
    return eventDays.includes(dayjs(day).format('DD.MM.YYYY'))
        || new Date().getHours() >= 16 && eventDays.includes(dayjs(day).subtract(1, 'day').format('DD.MM.YYYY'));
};

/**
 * **Для всех + Заявка в будни + Заявка в праздники**
 * @param {Date} day
 * @param {string[]} eventDays - правздники, список дат в формате `['DD.MM.YYYY', ...]`
 * @returns {boolean} true если день недоступен
 */
const isCommonRules = (day, eventDays = EVENT_DAYS) => isTodayAndBefore(day) || isDayAfterTomorrow(day) || blockNextMonday(day) || isAfter2Weeks(day) || isEventDays(day, eventDays);

/**
 * **Правила для доставки день в день**
 * @param {Date} day
 * @param {string[]} eventDays - правздники, список дат в формате `['DD.MM.YYYY', ...]`
 * @returns {boolean} true если день недоступен
 */
const isD2DRules = (day, eventDays = EVENT_DAYS) => isBefore(day) || isAvailableForD2D(day) || isAfter2Weeks(day) || isEventDays(day, eventDays);

/**
 * Проверка, доступности дня для доставки день в день
 * Если заявка поступает до 14:00, то доставка доступна день в день<br>
 * Если заявка поступает после 14:00, то доставка доступна только на следующий день
 * @param {Date} day
 * @returns {boolean} true если день недоступен
 */
const isAvailableForD2D = (day) => {
    return new Date().getHours() >= 14 && (dayjs(day).format('DD.MM.YYYY') === dayjs().format('DD.MM.YYYY'));
}

/**
 * Проверка, что выбранный день соответствует доставке день в день
 * @param {string} day
 * @returns {boolean} true если соответствует доставке день в день
 * Заявка оформлена на текущий день до 14:00
 * Заявка оформлена с 14:00 на следующий день (работает с пн по чт включительно)
 */
const isD2D = (day) => {
    day = convertDateToEng(day);
    return (new Date().getHours() < 14 && (dayjs(day).format('DD.MM.YYYY') === dayjs().format('DD.MM.YYYY')))
    || (new Date().getHours() >= 14 && (dayjs(day).format('DD.MM.YYYY')) === dayjs().add(1, 'day').format('DD.MM.YYYY') && [1,2,3,4,5].includes(dayjs().add(1, 'day').day()));
}

/**
 * Проверка входит ли текущее время во временной слот
 * @param {String} timeslot
 * @returns {boolean} true если текущее время не входит в слот
 */
const isAvailableTimeslot = (timeslot) => {
    let [startTimeslot] = timeslot.split('-');
    return new Date().getHours() < +startTimeslot;
}

/**
 * Вернет `true` если дата входит в список
 * @param {Date} day
 * @param {string[]} dayList - список неодступных дней в формате `['DD.MM.YYYY', ...]`
 */
const isInList = (day, dayList) => {
    return dayList.includes(dayjs(day).format('DD.MM.YYYY'))
};

/**
 * Объект с доступными рабочими часами на каждый день недели
 * @typedef {Object.<number, string[]>} TWorkHours
 */

/**
 * Проверка дня недели на доступность по временным слотам, если не установлены - день недоступен
 * @param {Date} day
 * @param {TWorkHours} timeslots - доступные слоты времени для сб вс или для будней
 * @returns {boolean} true если день недоступен
 */
const isDisabledIfHasNoTimeSlots = (day, timeslots) => {
    if (WORKED_DAYS.includes(dayjs(day).format('DD.MM.YYYY'))) {
        return !timeslots[1].length
    }
    return !timeslots[day.getDay()]?.length;
};
/**
 * Проверка дня на блокировку через инфоблок "нерабочие дни" в админке
 * @param {Date} day
 * @param {number} officeId - офис
 * @param {object} officesRaw - объект офисов из getOfficesV2
 * @returns {boolean} true если день недоступен
 */
const isNonWorkingPeriod = (day, officeId, officesRaw = []) => {
    const office = officesRaw.filter((item) => item.id === officeId)[0];

    if (!office.attributes.schedule.nonWorkingPeriods.length) return false;

    let res;

    for (let i = 0; i < office?.attributes.schedule.nonWorkingPeriods.length; i++) {
        let nonWorkingPeriod = office?.attributes.schedule.nonWorkingPeriods[i];

        let start = nonWorkingPeriod.start.split(' ')[0];
        let end = nonWorkingPeriod.end.split(' ')[0];

        let period = [start, end].map(i => dayjs(new Date(i.split(' ')[0].replace(/\./g, '-').split('-').reverse().join('-'))))

        if (dayjs(day).isBetween(period[0], period[1], 'day', '[]')) {
            res = true;
            break;
        } else {
            res = false;
        }
    }
    return res;
}

/**
 * Оставляем уникальные временные слоты и сортируем их
 * @param {array} timeslots - массив со временными слотами
 * @returns {array} массив со всеми уникальными и отсортированными слотами
 */
const sortUniqTimeslots = (timeslots) => {
    return timeslots.map(slot => slot.trim()).filter(onlyUnique).sort(function (a, b) {
        if (parseInt(a.split("-")[0]) - parseInt(b.split("-")[0]) === 0) {
            return parseInt(a.split("-")[1]) - parseInt(b.split("-")[1]);
        } else {
            return parseInt(a.split("-")[0]) - parseInt(b.split("-")[0]);
        }
    });
}

/**
 * Объединяем слоты курьеров и мобильных банкиров
 * @param {object} city - объект города из getCitiesV2
 * @param {string} nameCourierField - название поля со слотами для курьера
 * @param {string} nameBankerField - название поля со слотами для мобильного банкира
 * @returns {slots} объединенный массив
 */
const mergeTimeslots = (courierTimeSlots, mbankerTimeslots) => {
    return mbankerTimeslots.concat(courierTimeSlots)
};

/**
 * Получение временных слотов
 * @param {object} city - объект города из getCitiesV2
 * @param {string} nameField - название поля со слотами
 * @returns {slots} массив со всеми слотами
 */
const getTimeslots = (city, nameField) => {
    return city.attributes[nameField] ? city.attributes[nameField].split(',').filter((item) => !!item) : [];
}

/**
 * Парсим рабочие часы в городе в объект, где индекс - день недели (0 - воскресенье)
 * @param {string} cityId
 * @param {object} citiesRaw - объект городов из getCitiesV2
 * @returns {TWorkHours} объект с рабочими часами
 */
const parseWorkTimes = (cityId, citiesRaw = [], isD2D, mbPriority) => {
    const city = citiesRaw.filter((item) => item.id === cityId)[0];

    if (!city) return {}

    let weekSlots, sundaySlots, saturdaySlots;

    let courierWeekSlots = [];
    let courierSundaySlots = [];
    let courierSaturdaySlots = [];
    let mbankerWeekSlots = [];
    let mbankerSundaySlots = [];
    let mbankerSaturdaySlots = [];

    // только курьер
    if (city.attributes.hasCourier && !city.attributes.hasMobileBanker) {
        courierWeekSlots = getTimeslots(city, 'deliveryTimeInterval');
        courierSundaySlots = getTimeslots(city, 'deliveryTimeIntervalSunday');
        courierSaturdaySlots = getTimeslots(city, 'deliveryTimeIntervalSaturday');
    }
    // только мобильный банкир
    if (!city.attributes.hasCourier && city.attributes.hasMobileBanker) {
        mbankerWeekSlots = getTimeslots(city, 'deliveryTimeIntervalMb');
        mbankerSundaySlots = getTimeslots(city, 'deliveryTimeIntervalSundayMb');
        mbankerSaturdaySlots = getTimeslots(city, 'deliveryTimeIntervalSaturdayMb');
    }
    // курьер и мобильный банкир
    if (city.attributes.hasCourier && city.attributes.hasMobileBanker) {
        courierWeekSlots = getTimeslots(city, 'deliveryTimeInterval');
        courierSundaySlots = getTimeslots(city, 'deliveryTimeIntervalSunday');
        courierSaturdaySlots = getTimeslots(city, 'deliveryTimeIntervalSaturday');

        mbankerWeekSlots = getTimeslots(city, 'deliveryTimeIntervalMb');
        mbankerSundaySlots = getTimeslots(city, 'deliveryTimeIntervalSundayMb');
        mbankerSaturdaySlots = getTimeslots(city, 'deliveryTimeIntervalSaturdayMb');
    }
    // если доставка день-в-день, то выводим только слоты мобильного банкира. если есть параметр приоритета мобильного банкира, то выводим только его
    if ((isD2D && city.attributes.d2dAvailableMB) || (city.attributes.hasCourier && city.attributes.hasMobileBanker && mbPriority)) {
        courierWeekSlots = [];
        courierSundaySlots = [];
        courierSaturdaySlots = [];
        mbankerWeekSlots = getTimeslots(city, 'deliveryTimeIntervalMb');
        mbankerSundaySlots = getTimeslots(city, 'deliveryTimeIntervalSundayMb');
        mbankerSaturdaySlots = getTimeslots(city, 'deliveryTimeIntervalSaturdayMb');
    }

    // объединяем и сортируем полученные массивы слотов
    weekSlots = sortUniqTimeslots(mergeTimeslots(courierWeekSlots, mbankerWeekSlots));
    sundaySlots = sortUniqTimeslots(mergeTimeslots(courierSundaySlots, mbankerSundaySlots));
    saturdaySlots = sortUniqTimeslots(mergeTimeslots(courierSaturdaySlots, mbankerSaturdaySlots));

    return city ? {
        0: sundaySlots,
        1: weekSlots,
        2: weekSlots,
        3: weekSlots,
        4: weekSlots,
        5: weekSlots,
        6: saturdaySlots,
    } : {};
};

/**
 * Конвертация даты в английский формат
 * @param {string} val - дата в российском формате
 * @returns {string} дата в английском формате
 */
const convertDateToEng = (val) => {
    return val.replace(/\./g, '-').split('-').reverse().join('-');
}

/**
 * Проверка блокировки доставки в городе (через админку)
 * @param {Date} day
 * @param {string} cityId
 * @param {object} citiesRaw - объект городов из getCitiesV2
 * @returns {boolean} true если доставка заблокирована
 */
const isBlockedDelivery = (day, cityId, citiesRaw = []) => {
    const city = citiesRaw.filter((item) => item.id === cityId)[0];

    let from = city.attributes.disablingDeliveryFrom;
    let to = city.attributes.disablingDeliveryTo;

    if (from && to) {
        return dayjs(day).isBetween(convertDateToEng(from), convertDateToEng(to), 'day', '[]');
    } else {
        return false;
    }
};

// Возвращает массив дат между двумя датами
const getDates = (startDate, endDate) => {
    const dates = []
    let currentDate = startDate

    const addDays = function (days) {
        const date = new Date(this.valueOf())
        date.setDate(date.getDate() + days)
        return date
    }

    while (currentDate <= endDate) {
        dates.push(dayjs(currentDate).format('DD.MM.YYYY'))
        currentDate = addDays.call(currentDate, 1)
    }

    return dates
}

/**
 * Проверка массовой блокировки доставки в городах (через админку)
 * @param {Date} day
 * @param {string} city
 * @param {array} blockedCities - список городов-исключений с заблокированными датами
 * @param {array} dates - заблокированные даты и диапазоны дат
 * @returns {boolean} true если доставка заблокирована
 */
const isBlockedDeliveryCommon = (day, city, commonBlockedInfo) => {
    let cities = commonBlockedInfo.cities;
    let dates = commonBlockedInfo.dates;

    let rangeDates = dates.map(date => {
        if (typeof date === 'object') return getDates(new Date(convertDateToEng(date.from)), new Date(convertDateToEng(date.to)));
        if (typeof date === 'string') return date;
    }).flat()

    if (!cities.includes(city.attributes.cityCode) && rangeDates.includes(dayjs(day).format('DD.MM.YYYY'))) {
        return true;
    } else {
        return false;
    }
};

/**
 * Разбивает рабочее время на 2 части (до и после обеда)
 * @param {{start: string, end: string}} workTimes
 * @param {{start: string, end: string}} breakTimes
 * @returns {string[]} Массив временных слотов
 */
const getSlots = (workTimes = {}, breakTimes = {}) => {
    const slots = [];
    if (!workTimes || !workTimes.start || !workTimes.end) {
        // pass
    } else if (breakTimes && breakTimes.start && breakTimes.end) {
        slots.push(`${workTimes.start}-${breakTimes.start}`, `${breakTimes.end}-${workTimes.end}`);
    } else {
        slots.push(`${workTimes.start}-${workTimes.end}`);
    }
    return slots;
};

/**
 * Парсим рабочие часы в офисе в объект, где индекс - день недели (0 - воскресенье)
 *
 * **не учитываются забронированные даты**
 * @param {string} officeId - офис
 * @param {object} officesRaw - объект офисов из getOfficesV2
 * @param {boolean} isCompany - расчет для компании или физлиц ('companies' / 'individuals')
 * @returns {TWorkHours} объект с рабочими часами
 */
const parseOfficeWorkTimes = (officeId, officesRaw = [], isCompany = false) => {
    const office = officesRaw.filter((item) => item.id === officeId)[0];
    const type = isCompany ? 'companies' : 'individuals';

    const breakTimes = office?.attributes.schedule[type]?.break;

    const weekSlots = getSlots(office?.attributes.schedule[type]?.week, breakTimes);

    return office ? {
        0: getSlots(office.attributes.schedule[type]?.sun, breakTimes),
        1: weekSlots,
        2: weekSlots,
        3: weekSlots,
        4: weekSlots,
        5: weekSlots,
        6: getSlots(office.attributes.schedule[type]?.sat, breakTimes),
    } : {};
};

/**
 * Проверка города на наличие доставки день в день
 * @param {number} cityId - город
 * @param {object} citiesRaw - объект городов из getCitiesV2
 * @returns {boolean} true если есть доставка день в день
 */
const checkHasD2D = (cityId, citiesRaw) => {
    const city = citiesRaw.filter((item) => item.id === cityId)[0];
    if (!city) return;
    return city.attributes.d2dAvailableMB && city.attributes.hasMobileBanker;
}

const getBlockedDeliveryDates = () => {
    return axios.get('/rest/v2/network/cities/delivery')
}

export {
    isCommonRules,
    isManager,
    isHolidays,
    isHolidaysRules,
    isMoscow,
    isBlockedRegionAndDays,
    isInList,
    isSunday,
    isDisabledIfHasNoTimeSlots,
    parseWorkTimes,
    isBlockedDelivery,
    isBlockedDeliveryCommon,
    isTodayAndBefore,
    isAfter2Weeks,
    parseOfficeWorkTimes,
    isNonWorkingPeriod,
    checkHasD2D,
    isD2DRules,
    isD2D,
    isAvailableTimeslot,
    getBlockedDeliveryDates
};
